import { useState, useMemo, useEffect } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Column, SortingRule, Cell, Row } from 'react-table'
import { generatePath, useHistory } from 'react-router-dom'
import type { HomeId } from '@racepointenergy/swc/scs/types'
import { useIntl, FormattedMessage } from 'react-intl'

import { getSortOptions } from 'components/Table/utils'
import { homeActiveConfigEnergySelector } from 'features/home/selectors'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import ROUTES from 'app/routes'
import {
  fetchHomeActiveConfigEnergyThunk,
  homeActiveConfigEnergyActions,
} from 'features/home/components/HomeEnergySystem/slice'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { Table, TableLoader, MobileSortBy } from 'components/Table'
import { messages } from './messages'
import { BreakerHeader, NameSpan } from './styles'
import { Panel } from 'features/home/types'
import { PanelBlock } from './PanelBlock'
import { H1 } from 'components/Typography'

interface HomeBreakerPanelsProps
  extends RouteComponentProps<{ homeId: HomeId }> {}

function HomeBreakerPanels({ match }: HomeBreakerPanelsProps) {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const [panelData, setPanelData] = useState<Panel[]>([])

  const { homeId } = match.params

  const currentRootId = useAppSelector(currentRootDomainIdSelector)

  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  useEffect(() => {
    if (homeId && !isHomeActiveConfigEnergyLoaded) {
      dispatch(fetchHomeActiveConfigEnergyThunk(homeId))
    }
  }, [dispatch, homeId, isHomeActiveConfigEnergyLoaded])

  
  useEffect(() => {
    if (
      homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels?.length
    ) {
      const e =
        homeActiveConfigEnergy.energyConfig?.panelConfig.breakerPanels.map(
          (panel) => {
            return {
              ...panel,
            }
          }
        )
      setPanelData(e)
    }
  }, [homeActiveConfigEnergy, setPanelData])

  useEffect(() => {
    return () => {
      dispatch(homeActiveConfigEnergyActions.clear())
    }
  }, [dispatch, homeId])

  const headers = useMemo(
    () => ({
      name: {
        label: formatMessage(messages.headerName),
        id: 'Name',
      },
      type: {
        label: formatMessage(messages.headerType),
        id: 'Type',
      },
      room: {
        label: formatMessage(messages.headerRoom),
        id: 'Room',
      },
      size: {
        label: formatMessage(messages.headerSize),
        id: 'Size',
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<Panel>[] = useMemo(
    () => [
      {
        id: headers.name.id,
        Header: <NameSpan>{headers.name.label}</NameSpan>,
        accessor: (row) => row.panelName,
        Cell: (cell: Cell<Panel>) => <NameSpan>{cell.value}</NameSpan>,
      },
      {
        id: headers.type.id,
        Header: headers.type.label,
        accessor: (row) => row.panelType,
      },
      {
        id: headers.room.id,
        Header: headers.room.label,
        accessor: (row) => row.roomName,
      },
      {
        id: headers.size.id,
        Header: headers.size.label,
        accessor: (row) => row.slotsNumber,
      },
    ],
    [headers]
  )
  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])
  const defaultSortBy = sortOptions[0]
  const [sortBy, setSortBy] = useState<SortingRule<Panel>>(defaultSortBy)

  const onRowClick = (row: Row<Panel>) => {
    const breakerPanelPath = generatePath(ROUTES.breakerPanel, {
      rootDomainId: currentRootId || '',
      homeId: homeId || ' ',
      panelId: row?.original?.id || '',
    })

    return history.push(breakerPanelPath)
  }

  return (
    <>
      <BreakerHeader>
        <H1>
          <FormattedMessage {...messages.breakerHeader} />
        </H1>
      </BreakerHeader>
      <MobileSortBy
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      <div>
        {!isHomeActiveConfigEnergyLoaded && <TableLoader />}

        {isHomeActiveConfigEnergyLoaded && (
          <Table
            columns={columns}
            data={panelData}
            defaultSortBy={defaultSortBy}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onRowClick={onRowClick}
            mobileViewComponent={PanelBlock}
          />
        )}
      </div>
    </>
  )
}

export default HomeBreakerPanels
